import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  padding: 20px;
`;

const LoginContainer = styled.div`
  background: white;
  border-radius: 20px;
  padding: 40px;
  width: 100%;
  max-width: 450px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    padding: 30px 20px;
    margin: 20px;
  }
`;

const Title = styled.h1`
  text-align: center;
  margin-bottom: 30px;
  color: #333;
  font-size: 32px;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-weight: 600;
  color: #333;
  font-size: 14px;
`;

const Input = styled.input`
  padding: 15px;
  border: 2px solid #e1e5e9;
  border-radius: 10px;
  font-size: 16px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #ff6b35;
    box-shadow: 0 0 0 3px rgba(255, 107, 53, 0.1);
  }
  
  &::placeholder {
    color: #aaa;
  }
`;

const Button = styled.button`
  background: linear-gradient(135deg, #ff6b35 0%, #e55a2b 100%);
  color: white;
  padding: 15px 30px;
  border: none;
  border-radius: 10px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 10px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(255, 107, 53, 0.3);
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const BackLink = styled.span`
  display: block;
  margin-top: 16px;
  color: #666;
  cursor: pointer;
  font-size: 14px;
  &:hover {
    color: #ff6b35;
  }
`;

const LinkText = styled.p`
  text-align: center;
  margin-top: 20px;
  color: #666;
  
  a {
    color: #ff6b35;
    text-decoration: none;
    font-weight: 600;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ErrorMessage = styled.div`
  background: #fee;
  color: #c33;
  padding: 15px;
  border-radius: 10px;
  margin-bottom: 20px;
  border-left: 4px solid #c33;
`;

const SuccessMessage = styled.div`
  background: #efe;
  color: #3c3;
  padding: 15px;
  border-radius: 10px;
  margin-bottom: 20px;
  border-left: 4px solid #3c3;
`;

const ForgotPasswordLink = styled.div`
  text-align: right;
  margin-top: -10px;
  margin-bottom: 10px;
  
  a {
    color: #ff6b35;
    text-decoration: none;
    font-size: 14px;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const LoginPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pending2FA, setPending2FA] = useState(null);
  const [code2FA, setCode2FA] = useState('');
  
  const { login, verify2FA } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.email || !formData.password) {
      setError('Заполните все поля');
      return;
    }

    setLoading(true);
    try {
      const result = await login(
        (formData.email || '').trim(),
        (formData.password || '').trim()
      );
      
      console.log('Login result:', result);
      
      if (result.success) {
        if (result.requiresTwoFactor && result.tempToken) {
          setPending2FA({ tempToken: result.tempToken, user: result.user });
          setSuccess(result.message || 'Введите код из приложения аутентификации');
          setFormData({ ...formData, password: '' });
          return;
        }
        
        setSuccess('Вход выполнен успешно! Перенаправляем...');
        setTimeout(() => {
          const isAdmin = result.user?.role === 'admin';
          navigate(isAdmin ? '/dashboard' : '/', { replace: true });
        }, 300);
      } else {
        if (result.requiresVerification) {
          // Редирект на страницу подтверждения email
          const params = new URLSearchParams();
          if (result.email) params.append('email', result.email);
          setError('Необходимо подтвердить email. Перенаправляем на страницу подтверждения...');
          setTimeout(() => {
            navigate(`/verify-email?${params.toString()}`);
          }, 2000);
        } else {
          setError(result.message || 'Произошла ошибка при входе');
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message || 'Произошла ошибка при входе');
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    if (!pending2FA || !code2FA.trim()) {
      setError('Введите 6-значный код');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const result = await verify2FA(pending2FA.tempToken, code2FA.trim());
      if (result.success) {
        setSuccess('Вход выполнен! Перенаправляем в админ-панель...');
        setTimeout(() => navigate('/dashboard', { replace: true }), 300);
      } else {
        setError(result.message || 'Неверный код');
      }
    } catch (err) {
      setError(err.message || 'Ошибка проверки кода');
    } finally {
      setLoading(false);
    }
  };

  const handleBackFrom2FA = () => {
    setPending2FA(null);
    setCode2FA('');
    setError('');
    setSuccess('');
  };

  return (
    <PageContainer>
      <LoginContainer>
        <Title>Вход</Title>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}
        
        {pending2FA ? (
          <Form onSubmit={handle2FASubmit}>
            <p style={{ marginBottom: 16, color: '#666' }}>
              Откройте приложение Google Authenticator (или другое) и введите 6-значный код.
            </p>
            <InputGroup>
              <Label htmlFor="code2fa">Код двухфакторной аутентификации</Label>
              <Input
                type="text"
                id="code2fa"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={8}
                value={code2FA}
                onChange={(e) => setCode2FA(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                autoFocus
              />
            </InputGroup>
            <Button type="submit" disabled={loading || code2FA.length < 6}>
              {loading ? 'Проверка...' : 'Подтвердить'}
            </Button>
            <BackLink onClick={handleBackFrom2FA}>← Назад к форме входа</BackLink>
          </Form>
        ) : (
        <Form onSubmit={handleSubmit}>
          <InputGroup>
            <Label htmlFor="email">Email</Label>
            <Input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Введите email"
              required
            />
          </InputGroup>

          <InputGroup>
            <Label htmlFor="password">Пароль</Label>
            <Input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Введите пароль"
              required
            />
          </InputGroup>

          <ForgotPasswordLink>
            <Link to="/forgot-password">Забыли пароль?</Link>
          </ForgotPasswordLink>

          <Button type="submit" disabled={loading}>
            {loading ? 'Вход...' : 'Войти'}
          </Button>
        </Form>
        )}

        <LinkText>
          Нет аккаунта? <Link to="/register">Зарегистрироваться</Link>
        </LinkText>
      </LoginContainer>
    </PageContainer>
  );
};

export default LoginPage; 